import type { OutputDescription } from "@smartsuite/automation-engine-api";

type OutputMenuOption =
  | {
      label: string;
      value: string;
      icon?: string;
      nestedOptions?: undefined;
    }
  | {
      label: string;
      icon?: string;
      nestedOptions: Array<{
        label: string;
        value: string;
        icon?: string;
      }>;
      value?: undefined;
    };

export const getOutputLabel = (output?: OutputDescription): string => {
  if (output === undefined) {
    return "";
  }

  return output.labelSuffix
    ? `${output.label}: ${output.labelSuffix}`
    : output.label;
};

export const buildOutputMenuOptions = (
  outputs: OutputDescription[],
): OutputMenuOption[] => {
  const groupedOutputs = new Map<string, OutputDescription[]>();

  outputs.forEach((output) => {
    const groupKey = output.groupFieldId ?? output.label;
    const group = groupedOutputs.get(groupKey) ?? [];
    group.push(output);
    groupedOutputs.set(groupKey, group);
  });

  return Array.from(groupedOutputs.values()).reduce<OutputMenuOption[]>(
    (menuOptions, group) => {
      const topLevelOutput = group.find(
        (output) => output.labelSuffix === undefined,
      );
      const groupLabel = topLevelOutput?.label ?? group[0]?.label ?? "";
      const isGroupedField = group[0]?.groupFieldId !== undefined;
      const hasSuffixes = group.some(
        (output) => output.labelSuffix !== undefined,
      );

      if (!isGroupedField && !hasSuffixes) {
        menuOptions.push(
          ...group.map((output) => ({
            label: getOutputLabel(output),
            value: output.outputId,
            icon: output.icon,
          })),
        );
        return menuOptions;
      }

      menuOptions.push({
        label: groupLabel,
        icon: topLevelOutput?.icon ?? group[0]?.icon,
        nestedOptions: group.map((output) => ({
          label: getOutputLabel(output),
          value: output.outputId,
          icon: output.icon,
        })),
      });

      return menuOptions;
    },
    [],
  );
};
